import { KeyboardEvent, useContext, useEffect, useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { MaptilerFeature, useMaptilerGeosearch } from "@nens/react/hooks";

import { ProjectConfigurationContext } from "@/pages/main-page";
import { useStatusQuery } from "@/queries/query-status";

export function useGeocoderInput() {
  const { mainMap } = useMap();
  const { location, clearPolygon } = useContext(ProjectConfigurationContext);
  const statusResponse = useStatusQuery();
  const maptilerApiKey =
    statusResponse.isSuccess && statusResponse.data.authenticated
      ? statusResponse.data.maptilerApiKey
      : "";

  const [searchString, actualSetSearchString] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(0);
  const [disableGeocoder, setDisableGeocoder] = useState(false);

  const setSearchString = (s: string) => {
    setDisableGeocoder(false);
    if (s !== searchString) {
      setSelectedPlace(0);
    }
    actualSetSearchString(s);
  };

  useEffect(() => {
    setDisableGeocoder(true);
    actualSetSearchString("");
  }, [location]);

  const geocoderResponse = useMaptilerGeosearch({
    maptilerApiKey,
    searchString,
    locale: "nl",
    bbox: [3.314971, 50.803721, 7.092053, 53.510403], // Netherlands
    enabled: !!(!disableGeocoder && searchString && maptilerApiKey),
  });

  const setMaptilerFeature = (feature?: MaptilerFeature) => {
    if (feature) {
      mainMap?.fitBounds(feature.bbox, { duration: 1500, padding: 20, maxZoom: 15 });
      setSearchString("");
    }
  };

  const cancel = () => {
    clearPolygon();
    setSearchString("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // arrow up/down button should select next/previous list element
    if (e.code === "ArrowUp") {
      setSelectedPlace((place) => Math.max(0, place - 1));
      e.preventDefault();
    } else if (e.code === "ArrowDown") {
      setSelectedPlace((place) => Math.min(place + 1, geocoderResponse.data?.features.length ?? 0));
      e.preventDefault();
    } else if (e.code === "Enter" && selectedPlace > 0) {
      setMaptilerFeature(geocoderResponse.data?.features[selectedPlace - 1]);
      e.preventDefault();
    } else if (e.code === "Escape" && searchString) {
      setSearchString("");
      e.preventDefault();
    }
  };

  const geocoderFeatures: MaptilerFeature[] = geocoderResponse.data?.features ?? [];

  return {
    geocoderFeatures,
    searchString,
    setSearchString,
    setMaptilerFeature,
    selectedPlace,
    onKeyDown,
    cancel,
  };
}
